import { ChangeDetectorRef, Component, ElementRef, OnInit, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { OrdersService } from 'src/app/feature-modules/orders/shared/orders.service';
import { DialogContentExampleDialog } from 'src/app/feature-modules/orders/components/title/title.component';
import { FilterDateRangeService } from 'src/app/services/filterdaterange.service';
import { IUserTerritoryFromAPI } from 'src/app/core/interfaces/app.interface';
import { UserService } from 'src/app/core/user.service';


interface District {
  id: string;
  name: string;
}

interface Territory {
  id: string;
  districtId: string;
  name: string;
}

interface Customer {
  id: string;
  companyName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  shipToNumber: string;
  soldToNumber: string;
  customerId: string;
  territoryId: string;
}


@Component({
  selector: 'app-territory-view',
  templateUrl: './territory-view.component.html',
  styleUrl: './territory-view.component.scss'
})
export class TerritoryViewComponent implements OnInit {

  filterForm!: FormGroup;
  isBusy = false;

  districts: District[] = [];
  territories: Territory[] = [];
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];

  districtList: string[] = [];
  filteredTerritories: IUserTerritoryFromAPI[] = [];
  selectedDistrict = '';
  isRestoringView = false;

  currentYear = new Date().getFullYear();
  activeTab: 'browse' | 'search' = 'browse';
  viewMode: 'grid' | 'list' = 'grid';

  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  dataSource: any[] = [];
  pagedData: any[] = [];

  isExpanded = false;

  constructor(
    private fb: FormBuilder,
    public ordersService: OrdersService,
    private router: Router,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private renderer: Renderer2,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private filterDateRangeService: FilterDateRangeService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.initializeForm();

    const territories = this.userService.currentUser?.territories || [];

    this.districtList = [...new Set(territories.map(t => t.territoryId.substring(0, 5)))];

    const isReturning = sessionStorage.getItem('territoryViewLoading') === 'true';
    const state = sessionStorage.getItem('territoryViewState');

    if (isReturning && state) {
      this.isBusy = true;
      this.isRestoringView = true;

      const savedState = JSON.parse(state);

      this.filteredTerritories = territories.filter(t => t.territoryId.startsWith(savedState.district));

      this.filterForm.patchValue(
        {
          district: savedState.district,
          territory: savedState.territory,
          searchTerm: savedState.searchTerm || ''
        },
        { emitEvent: false }
      );

      this.dataSource = savedState.customers || [];
      this.pageIndex = savedState.pageIndex || 0;

      this.filterCustomers();

      setTimeout(() => {
        this.isBusy = false;
        this.isRestoringView = false;
        sessionStorage.removeItem('territoryViewLoading');
        this.cdr.detectChanges();
      }, 500);

      return;
    }

    // Default first district + first territory
    if (this.districtList.length > 0) {
      const defaultDistrict = this.districtList[0];

      this.filterForm.patchValue({ district: defaultDistrict }, { emitEvent: false });
      this.onDistrictChange(defaultDistrict);
    }
  }

  toggleSidenav(): void {
    this.isExpanded = !this.isExpanded;
  }

  setBrowseTab(): void {
    this.activeTab = 'browse';
    //  Browse tab can go to territorydashboard
    sessionStorage.removeItem('athenaSearchMode');
    console.log(' Browse tab active - territorydashboard allowed');
  }

  setSearchTab(): void {
    this.activeTab = 'search';
    //  Search tab should never go to territorydashboard
    sessionStorage.setItem('athenaSearchMode', 'true');
    console.log(' Search tab active - territorydashboard blocked');
  }

  /** Call this after loading data */
  updatePagedData() {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.pagedData = this.filteredCustomers.slice(startIndex, endIndex);
  }

  /** Page Change */
  nextPage() {
    if (!this.isLastPage()) {
      this.pageIndex++;
      this.updatePagedData();
    }
  }

  prevPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
      this.updatePagedData();
    }
  }

  goToFirstPage() {
    this.pageIndex = 0;
    this.updatePagedData();
  }

  goToLastPage() {
    this.pageIndex = Math.floor((this.totalItems - 1) / this.pageSize);
    this.updatePagedData();
  }

  isLastPage(): boolean {
    return (this.pageIndex + 1) * this.pageSize >= this.totalItems;
  }

  /** Page size change */
  onPageSizeChange(event: any) {
    this.pageSize = Number(event.target.value);
    this.pageIndex = 0; //  reset to first page
    this.updatePagedData();
  }

  get paginationRange(): string {
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min((this.pageIndex + 1) * this.pageSize, this.totalItems);
    return `${start} - ${end} of ${this.totalItems}`;
  }

  private initializeForm() {
    this.filterForm = this.fb.group({
      district: [''],
      territory: [''],
      searchTerm: ['']
    });

    this.filterForm.get('district')?.valueChanges.subscribe((districtId: string) => {
      if (!districtId || this.isRestoringView) {
        return;
      }
      this.onDistrictChange(districtId);
    });

    this.filterForm.get('territory')?.valueChanges.subscribe((territoryId: string) => {
      if (!territoryId || this.isRestoringView) {
        return;
      }

      const selectedTerritory = this.filteredTerritories.find(t => t.territoryId === territoryId);

      if (!selectedTerritory) {
        this.clearCustomerData();
        return;
      }

      this.isBusy = true;

      // cleaer old data immediately to avoid showing stale data
      this.clearCustomerData();

      this.ordersService.getShipTosByTerritory(selectedTerritory.territoryId, selectedTerritory.systemId).subscribe({
        next: () => {
          this.dataSource = [...(this.ordersService.orderStatusList || [])];
          this.pageIndex = 0;
          this.filterCustomers();
          this.isBusy = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error(err);
          this.clearCustomerData();
          this.isBusy = false;
          this.cdr.detectChanges();
        }
      });
    });

    this.filterForm.get('searchTerm')?.valueChanges.subscribe(() => {
      this.pageIndex = 0;
      this.filterCustomers();
    });
  }

  private onDistrictChange(districtId: string) {
    const territories = this.userService.currentUser?.territories || [];

    this.filteredTerritories = territories.filter(t => t.territoryId.startsWith(districtId));

    // CLEAR OLD DATA IMMEDIATELY
    this.clearCustomerData();

    if (this.filteredTerritories.length > 0) {
      this.filterForm.patchValue({ territory: this.filteredTerritories[0].territoryId }, { emitEvent: true });
    } else {
      this.filterForm.patchValue({ territory: '' }, { emitEvent: false });
    }
  }

  private filterCustomers() {
    let filtered = [...this.dataSource];

    const searchTerm = this.filterForm.get('searchTerm')?.value?.toLowerCase()?.trim() || '';

    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.orders?.[0]?.shipToName?.toLowerCase()?.includes(searchTerm) ||
        customer.shipToNumber?.toLowerCase()?.includes(searchTerm) ||
        customer.orders?.[0]?.soldToNumber?.toLowerCase()?.includes(searchTerm) ||
        customer.shipToAddress?.city?.toLowerCase()?.includes(searchTerm) ||
        customer.shipToAddress?.state?.toLowerCase()?.includes(searchTerm) ||
        customer.shipToAddress?.postalCode?.toLowerCase()?.includes(searchTerm) ||
        customer.shipToAddress?.country?.toLowerCase()?.includes(searchTerm)
      );
    }

    this.filteredCustomers = filtered;
    this.totalItems = filtered.length;

    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    this.pagedData = filtered.slice(startIndex, endIndex);
  }

  private getPaginatedCustomers(customers: Customer[]): Customer[] {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return customers.slice(startIndex, endIndex);
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.filterCustomers();
  }

  viewOrders(customer: any) {
    console.log('🔗 View Orders clicked - Navigating to Territory Dashboard with ShipTo:', customer.shipToNumber);

    sessionStorage.removeItem('athenaSearchMode');

    // Subscribe to get dates from filterDateRangeService
    this.filterDateRangeService.dateRangeSelected$.subscribe(dateRange => {
      let fromDate: string;
      let toDate: string;

      // Use dates from service if available, otherwise use 90-day default
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        console.log('Using dates from service:', { startDate: dateRange.startDate, endDate: dateRange.endDate });
        fromDate = dateRange.startDate;
        toDate = dateRange.endDate;
      } else {
        console.log('No dates in service, using 90-day default');
        // Default: 90 days
        const today = new Date();
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(today.getDate() - 90);

        fromDate = this.formatDateToYMD(ninetyDaysAgo);
        toDate = this.formatDateToYMD(today);
      }

      console.log('Final dates for navigation:', { fromDate, toDate });

      sessionStorage.setItem(
        'territoryViewState',
        JSON.stringify({
          district: this.filterForm.get('district')?.value,
          territory: this.filterForm.get('territory')?.value,
          searchTerm: this.filterForm.get('searchTerm')?.value,
          customers: this.dataSource,
          pageIndex: this.pageIndex
        })
      );

      // Navigate to territory dashboard with shipToNumber and dates
      this.router.navigate(['/territorydashboard'], {
        state: {
          shipToNumber: customer.shipToNumber,
          searchValue: customer.shipToNumber,
          fromDate,
          toDate
        }
      });
    });
  }

  private formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(DialogContentExampleDialog, { panelClass: 'custom-dialog-container' });
    dialogRef.afterClosed().subscribe(result => console.log(`Dialog result: ${result}`));
  }

  private loadCustomersByTerritory(territoryId: string) {
    const selectedTerritory = this.filteredTerritories.find(t => t.territoryId === territoryId);

    if (!selectedTerritory) {
      this.clearCustomerData();
      return;
    }

    this.isBusy = true;

    // IMPORTANT: clear old UI data before new API call
    this.clearCustomerData();

    // IMPORTANT: clear old service data also
    this.ordersService.orderStatusList = [];

    this.ordersService.getOrdersByTerritory(selectedTerritory.territoryId, selectedTerritory.systemId).subscribe({
      next: () => {
        const latestOrders = this.ordersService.orderStatusList || [];
        this.dataSource = [...latestOrders];
        this.pageIndex = 0;
        this.filterCustomers();
        this.isBusy = false;

        console.log('Loaded Territory:', selectedTerritory.territoryId);
        console.log('Loaded Customer Count:', this.dataSource.length);

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('getOrdersByTerritory Error:', err);

        // IMPORTANT: do not show previous data on error
        this.clearCustomerData();

        this.isBusy = false;
        this.cdr.detectChanges();
      }
    });
  }

  private clearCustomerData() {
    this.dataSource = [];
    this.filteredCustomers = [];
    this.pagedData = [];
    this.totalItems = 0;
    this.pageIndex = 0;
    this.filterForm.get('searchTerm')?.setValue('');
  }

}
